import React, { useEffect, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "../css/Profile.css";
import "../css/SeasonEndLeaderboard.css";
import useLeaderboard from "../hooks/useLeaderboard";
import useSeason from "../hooks/useSeason";
import { useUser } from "../hooks/useUser";
import { useTeam } from "../hooks/useTeam";
import { usePlayer } from "../hooks/usePlayer";
import { useNavigate } from "react-router-dom";
import UnkownPlayer from "../assets/unknownPlayer.png";
import UnknownTeam from "../assets/Unknown.png";

const SeasonEndLeaderboard = () => {
  const {
    leaderboard,
    loading,
    fetchLeaderboardForSeason,
    fetchPremiumLeaderboardForSeason,
    fetchLeaderboardForLeague,
  } = useLeaderboard();
  const { currentSeason, fetchCurrentSeason, seasons, fetchSeasons } =
    useSeason();
  const { user } = useUser();
  const { teams, fetchAllTeams } = useTeam();
  const { players, fetchAllPlayers } = usePlayer();
  const [view, setView] = useState("global");
  const [firstPlace, setFirstPlace] = useState(null);
  const navigate = useNavigate();

  const formatDate = (unixTimestamp) => {
    const date = new Date(unixTimestamp);
    return date.toLocaleDateString("da-DK", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const findSeasonNameByDate = (date) => {
    const parsedDate = new Date(date);
    const season = seasons?.find((season) => {
      const start = new Date(season.start);
      const end = new Date(season.end);
      return parsedDate >= start && parsedDate <= end;
    });
    return season ? season.name : "Unknown Season";
  };

  useEffect(() => {
    fetchCurrentSeason();
    fetchAllTeams();
    fetchAllPlayers();
    fetchSeasons();
  }, []);

  useEffect(() => {
    if (!currentSeason) return;

    if (view === "global") {
      fetchLeaderboardForSeason(currentSeason._id);
    } else if (view === "premium") {
      fetchPremiumLeaderboardForSeason(currentSeason._id);
    } else if (view === "league" && user?.league) {
      const leagueId =
        typeof user.league === "object" ? user.league._id : user.league;
      fetchLeaderboardForLeague(leagueId, currentSeason._id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, currentSeason, user]);

  useEffect(() => {
    if (leaderboard && leaderboard.length > 0) {
      let firstPlaceData;

      // Check if this is league view - league data is flat, not nested
      if (view === "league") {
        // Filter out inactive users first
        const activeUsers = leaderboard.filter((item) => {
          const isCurrentMember = item.isCurrentMember;
          const isBanned = item.isBanned;
          return !(isCurrentMember === false || isBanned);
        });

        if (activeUsers.length > 0) {
          // League data is already flat - just use it directly and add league from context
          firstPlaceData = {
            ...activeUsers[0],
            league: activeUsers[0].league || user?.league,
          };
        } else {
          setFirstPlace(null);
          return;
        }
      } else if (leaderboard[0].user) {
        // Other view types with nested user data
        firstPlaceData = {
          ...leaderboard[0],
          ...leaderboard[0].user,
          points: leaderboard[0].points,
          teamCount: leaderboard[0].teamCount,
          winCount: leaderboard[0].winCount,
        };
      } else {
        // Global/Premium - user data at top level
        firstPlaceData = leaderboard[0];
      }

      setFirstPlace(firstPlaceData);
    } else {
      setFirstPlace(null);
    }
  }, [leaderboard, teams, players, view, user]);

  const changeView = (newView) => {
    setView(newView);
  };

  const goToProfile = () => {
    if (firstPlace && firstPlace.userNumber) {
      navigate(`/profile/${firstPlace.userNumber}`);
    }
  };

  const getTeamImage = () => {
    if (!firstPlace?.favoriteTeam || !teams || teams.length === 0) {
      return UnknownTeam;
    }
    const teamId =
      typeof firstPlace.favoriteTeam === "object"
        ? firstPlace.favoriteTeam._id
        : firstPlace.favoriteTeam;
    const team = teams.find(
      (t) => t._id === teamId || t._id === teamId?.toString()
    );
    return team?.image || UnknownTeam;
  };

  const getPlayerImage = () => {
    if (!firstPlace?.favoritePlayer || !players || players.length === 0) {
      return UnkownPlayer;
    }
    const playerId =
      typeof firstPlace.favoritePlayer === "object"
        ? firstPlace.favoritePlayer._id
        : firstPlace.favoritePlayer;
    const player = players.find(
      (p) => p._id === playerId || p._id === playerId?.toString()
    );
    return player?.image || UnkownPlayer;
  };

  if (loading) {
    return (
      <div className="season-end-leaderboard-wrapper">
        <div className="season-end-header">
          <h1 className="season-end-title">Season Winners</h1>
          <div className="view-button-container">
            <button
              className={`view-button ${view === "global" ? "active" : ""}`}
              onClick={() => changeView("global")}
            >
              Global
            </button>
            <button
              className={`view-button ${view === "premium" ? "active" : ""}`}
              onClick={() => changeView("premium")}
            >
              Premium
            </button>
            {user?.league && (
              <button
                className={`view-button ${view === "league" ? "active" : ""}`}
                onClick={() => changeView("league")}
              >
                League
              </button>
            )}
          </div>
        </div>
        <div className="season-end-content-box">
          <div className="loader" />
        </div>
      </div>
    );
  }

  if (!firstPlace || !firstPlace.username) {
    return (
      <>
        <div className="view-button-container" style={{ marginBottom: "1vw" }}>
          <button
            className={`view-button ${view === "global" ? "active" : ""}`}
            onClick={() => changeView("global")}
          >
            Global
          </button>
          <button
            className={`view-button ${view === "premium" ? "active" : ""}`}
            onClick={() => changeView("premium")}
          >
            Premium
          </button>
          {user?.league && (
            <button
              className={`view-button ${view === "league" ? "active" : ""}`}
              onClick={() => changeView("league")}
            >
              League
            </button>
          )}
        </div>
        <div className="profile-information-main-container">
          <div className="profile-information-content">
            <p style={{ textAlign: "center", padding: "2vw" }}>
              No leaderboard data available
            </p>
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="season-end-leaderboard-wrapper">
      <div className="season-end-header">
        <h1 className="season-end-title">Season Winners</h1>
        <div className="view-button-container">
          <button
            className={`view-button ${view === "global" ? "active" : ""}`}
            onClick={() => changeView("global")}
          >
            Global
          </button>
          <button
            className={`view-button ${view === "premium" ? "active" : ""}`}
            onClick={() => changeView("premium")}
          >
            Premium
          </button>
          {user?.league && (
            <button
              className={`view-button ${view === "league" ? "active" : ""}`}
              onClick={() => changeView("league")}
            >
              League
            </button>
          )}
        </div>
      </div>

      <div
        className="profile-information-container"
        style={{ alignItems: "center", height: "100%" }}
        onClick={goToProfile}
      >
        <div className="profile-image-container">
          <img className="profile-image-team" alt="" src={getTeamImage()} />
          <img className="profile-image-player" alt="" src={getPlayerImage()} />
        </div>
        <div className="profile-information-content" style={{ flex: 1 }}>
          <table className="profile-stats-table">
            <thead>
              <tr>
                <th
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                  }}
                >
                  <span>{firstPlace.username}</span>
                </th>
                <th
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    fontSize: "1vw",
                    justifyContent: "flex-start",
                  }}
                >
                  {firstPlace.isPremium && (
                    <span className="profile-user-tag">Premium</span>
                  )}
                </th>
              </tr>
            </thead>
            <tbody className="small">
              <tr>
                <td>Points</td>
                <td>{firstPlace.points.toFixed(2)}</td>
              </tr>
              {firstPlace.dateJoined && (
                <tr>
                  <td>Date Joined</td>
                  <td>
                    {formatDate(firstPlace.dateJoined)} (
                    {findSeasonNameByDate(firstPlace.dateJoined)})
                  </td>
                </tr>
              )}
              <tr>
                <td>League</td>
                <td>
                  {firstPlace.league ? firstPlace.league.name : "No League"}
                </td>
              </tr>
              {firstPlace.teamCount !== undefined && (
                <tr>
                  <td>Events Participated</td>
                  <td>{firstPlace.teamCount}</td>
                </tr>
              )}
              {firstPlace.winCount !== undefined && (
                <tr>
                  <td>Events Won</td>
                  <td>{firstPlace.winCount}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SeasonEndLeaderboard;
